import type { AlgebraicTypeType, ProductType } from 'spacetimedb';
import { reducers as generatedReducers } from '../spacetime/generated';
import type { SpaceTimeClient } from '../spacetime/SpaceTimeClient';

type ReducerDefinition = {
  readonly name: string;
  readonly accessorName: string;
  readonly paramsType: ProductType;
};

type ReducerElement = {
  readonly name: string;
  readonly algebraicType: AlgebraicTypeType;
};

const reducerDefinitions = Object.values(generatedReducers as Record<string, unknown>)
  .filter(isReducerDefinition)
  .sort((left, right) => left.name.localeCompare(right.name));

const reducerByName = new Map<string, ReducerDefinition>();
for (const reducer of reducerDefinitions) {
  reducerByName.set(reducer.name.toLowerCase(), reducer);
  reducerByName.set(reducer.accessorName.toLowerCase(), reducer);
}

function isReducerDefinition(value: unknown): value is ReducerDefinition {
  if (!value || typeof value !== 'object') {
    return false;
  }

  const candidate = value as Partial<ReducerDefinition>;
  return (
    typeof candidate.name === 'string' &&
    typeof candidate.accessorName === 'string' &&
    !!candidate.paramsType &&
    typeof candidate.paramsType === 'object'
  );
}

export function listReducerSignatures(): string[] {
  return reducerDefinitions.map(formatReducerSignature);
}

export async function executeReducerByCommand(
  client: SpaceTimeClient,
  reducerName: string,
  rawArgs: string[]
): Promise<void> {
  const reducer = reducerByName.get(reducerName.toLowerCase());
  if (!reducer) {
    throw new Error(`Unknown reducer: ${reducerName}`);
  }

  const parsedArgs = parseReducerArgs(reducer, rawArgs);
  const connection = await client.ensureConnection();
  connection.callReducerWithParams(
    reducer.name,
    reducer.paramsType,
    formatReducerArgs(reducer.paramsType, parsedArgs),
    'FullUpdate'
  );
}

export function getReducerUsage(reducerName: string): string {
  const reducer = reducerByName.get(reducerName.toLowerCase());
  return reducer ? formatReducerSignature(reducer) : reducerName;
}

function formatReducerSignature(reducer: ReducerDefinition): string {
  const params = getReducerElements(reducer.paramsType)
    .map((element) => `${element.name}: ${formatType(element.algebraicType)}`)
    .join(', ');
  return `${reducer.name}(${params})`;
}

function getReducerElements(paramsType: ProductType): ReducerElement[] {
  const elements = paramsType.elements ?? [];
  if (
    elements.length === 1 &&
    elements[0]?.name === 'args' &&
    elements[0].algebraicType.tag === 'Product'
  ) {
    return (elements[0].algebraicType.value.elements ?? [])
      .filter((element): element is ReducerElement => typeof element.name === 'string');
  }

  return elements.filter((element): element is ReducerElement => typeof element.name === 'string');
}

function formatType(type: AlgebraicTypeType): string {
  switch (type.tag) {
    case 'String':
      return 'string';
    case 'Bool':
      return 'bool';
    case 'I8':
    case 'I16':
    case 'I32':
    case 'I64':
    case 'I128':
    case 'I256':
    case 'U8':
    case 'U16':
    case 'U32':
    case 'U64':
    case 'U128':
    case 'U256':
    case 'F32':
    case 'F64':
      return type.tag.toLowerCase();
    case 'Array':
      return `${formatType(type.value)}[]`;
    case 'Product':
      return 'object';
    case 'Ref':
      return 'ref';
    case 'Sum': {
      const variants = type.value.variants ?? [];
      const areUnitVariants = variants.every(
        (variant) =>
          variant.algebraicType.tag === 'Product' &&
          variant.algebraicType.value.elements.length === 0
      );
      if (areUnitVariants) {
        return variants.map((variant) => variant.name ?? '?').join(' | ');
      }
      return 'enum';
    }
  }
}

function parseReducerArgs(reducer: ReducerDefinition, rawArgs: string[]): Record<string, unknown> {
  const elements = getReducerElements(reducer.paramsType);
  if (elements.length === 0) {
    if (rawArgs.length > 0) {
      throw new Error(`Usage: /reducer ${formatReducerSignature(reducer)}`);
    }
    return {};
  }

  if (rawArgs.length === 1) {
    const joined = rawArgs[0].trim();
    if (joined.startsWith('{') && joined.endsWith('}')) {
      return parseJsonObjectArgs(joined, elements, reducer);
    }
  }

  if (rawArgs.length !== elements.length) {
    throw new Error(
      `Usage: /reducer ${formatReducerSignature(reducer)} or /reducer ${reducer.name} {"arg":...}`
    );
  }

  const parsed: Record<string, unknown> = {};
  for (let index = 0; index < elements.length; index += 1) {
    const element = elements[index];
    parsed[element.name] = parseValue(rawArgs[index], element.algebraicType, element.name);
  }
  return parsed;
}

function parseJsonObjectArgs(
  rawJson: string,
  elements: ReducerElement[],
  reducer: ReducerDefinition
): Record<string, unknown> {
  let parsed: unknown;
  try {
    parsed = JSON.parse(rawJson);
  } catch {
    throw new Error(`Invalid JSON object for ${reducer.name}`);
  }

  if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
    throw new Error(`Expected a JSON object for ${reducer.name}`);
  }

  const record = parsed as Record<string, unknown>;
  const result: Record<string, unknown> = {};
  for (const element of elements) {
    if (!Object.hasOwn(record, element.name)) {
      throw new Error(`Missing argument: ${element.name}`);
    }
    result[element.name] = coerceJsonValue(record[element.name], element.algebraicType, element.name);
  }
  return result;
}

function parseValue(rawValue: string, type: AlgebraicTypeType, argName: string): unknown {
  switch (type.tag) {
    case 'String':
      return parseString(rawValue);
    case 'Bool':
      if (rawValue === 'true') return true;
      if (rawValue === 'false') return false;
      throw new Error(`Argument ${argName} must be true or false`);
    case 'I8':
    case 'I16':
    case 'I32':
    case 'U8':
    case 'U16':
    case 'U32':
      return parseInteger(rawValue, argName);
    case 'F32':
    case 'F64':
      return parseFloatNumber(rawValue, argName);
    case 'I64':
    case 'U64':
    case 'I128':
    case 'U128':
    case 'I256':
    case 'U256':
      return parseBigIntValue(rawValue, argName);
    case 'Sum': {
      const unitVariant = findUnitVariant(type, rawValue);
      if (unitVariant) {
        return { tag: unitVariant };
      }
      throw new Error(`Argument ${argName} must be one of: ${listUnitVariants(type).join(', ')}`);
    }
    case 'Array':
    case 'Product':
      return parseJsonValue(rawValue, argName);
    case 'Ref':
      throw new Error(`Argument ${argName} uses an unsupported ref type`);
  }
}

function coerceJsonValue(value: unknown, type: AlgebraicTypeType, argName: string): unknown {
  switch (type.tag) {
    case 'String':
      if (typeof value !== 'string') throw new Error(`Argument ${argName} must be a string`);
      return value;
    case 'Bool':
      if (typeof value !== 'boolean') throw new Error(`Argument ${argName} must be a boolean`);
      return value;
    case 'I8':
    case 'I16':
    case 'I32':
    case 'U8':
    case 'U16':
    case 'U32':
      if (typeof value !== 'number' || !Number.isInteger(value)) {
        throw new Error(`Argument ${argName} must be an integer`);
      }
      return value;
    case 'F32':
    case 'F64':
      if (typeof value !== 'number') {
        throw new Error(`Argument ${argName} must be a number`);
      }
      return value;
    case 'I64':
    case 'U64':
    case 'I128':
    case 'U128':
    case 'I256':
    case 'U256':
      if (typeof value === 'number' && Number.isInteger(value)) {
        return BigInt(value);
      }
      if (typeof value === 'string') {
        return parseBigIntValue(value, argName);
      }
      throw new Error(`Argument ${argName} must be an integer or integer string`);
    case 'Sum':
      if (typeof value === 'string') {
        const unitVariant = findUnitVariant(type, value);
        if (unitVariant) {
          return { tag: unitVariant };
        }
      }
      if (value && typeof value === 'object' && !Array.isArray(value) && 'tag' in value) {
        return value;
      }
      throw new Error(`Argument ${argName} must be a reducer enum value`);
    case 'Array':
      if (!Array.isArray(value)) {
        throw new Error(`Argument ${argName} must be an array`);
      }
      return value;
    case 'Product':
      if (!value || typeof value !== 'object' || Array.isArray(value)) {
        throw new Error(`Argument ${argName} must be an object`);
      }
      return value;
    case 'Ref':
      throw new Error(`Argument ${argName} uses an unsupported ref type`);
  }
}

function parseString(rawValue: string): string {
  if (
    (rawValue.startsWith('"') && rawValue.endsWith('"')) ||
    (rawValue.startsWith("'") && rawValue.endsWith("'"))
  ) {
    try {
      return JSON.parse(rawValue.startsWith("'") ? `"${rawValue.slice(1, -1)}"` : rawValue);
    } catch {
      return rawValue.slice(1, -1);
    }
  }
  return rawValue;
}

function parseInteger(rawValue: string, argName: string): number {
  const parsed = Number(rawValue);
  if (!Number.isInteger(parsed)) {
    throw new Error(`Argument ${argName} must be an integer`);
  }
  return parsed;
}

function parseFloatNumber(rawValue: string, argName: string): number {
  const parsed = Number(rawValue);
  if (!Number.isFinite(parsed)) {
    throw new Error(`Argument ${argName} must be a number`);
  }
  return parsed;
}

function parseBigIntValue(rawValue: string, argName: string): bigint {
  try {
    return BigInt(rawValue);
  } catch {
    throw new Error(`Argument ${argName} must be an integer`);
  }
}

function parseJsonValue(rawValue: string, argName: string): unknown {
  try {
    return JSON.parse(rawValue);
  } catch {
    throw new Error(`Argument ${argName} must be valid JSON`);
  }
}

function findUnitVariant(type: Extract<AlgebraicTypeType, { tag: 'Sum' }>, rawValue: string): string | null {
  const match = type.value.variants.find(
    (variant) =>
      variant.name?.toLowerCase() === rawValue.toLowerCase() &&
      variant.algebraicType.tag === 'Product' &&
      variant.algebraicType.value.elements.length === 0
  );
  return match?.name ?? null;
}

function listUnitVariants(type: Extract<AlgebraicTypeType, { tag: 'Sum' }>): string[] {
  return type.value.variants
    .filter(
      (variant) =>
        variant.algebraicType.tag === 'Product' &&
        variant.algebraicType.value.elements.length === 0 &&
        typeof variant.name === 'string'
    )
    .map((variant) => variant.name as string);
}

function formatReducerArgs(paramsType: ProductType, args: Record<string, unknown>): Record<string, unknown> {
  const elements = paramsType.elements ?? [];
  if (elements.length === 1 && elements[0]?.name === 'args') {
    return { args };
  }
  return args;
}
